import { colors } from '@globals/themes/defaultTheme'
import { onFocus, onHover, spacing } from '@globals/themes/themeConstants'
import { $generateHtmlFromNodes } from '@lexical/html'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps, styled } from '@mui/material/styles'
import { EditorState, LexicalEditor } from 'lexical'
import React, { FC, PropsWithChildren, useState } from 'react'
import AutoFocusPlugin from './AutoFocusPlugin'
import FocusPlugin from './FocusPlugin'
import KudosToolbar from './KudosToolbar'
import { MentionNode } from './MentionNode'
import MentionsPlugin from './MentionsPlugin'
import { SetValuePlugin } from './SetValuePlugin'
import SubmitOnEnterPlugin from './SubmitOnEnterPlugin'

type ContentEditableProps = {
  error: boolean
}

const MuiContentEditable = styled(ContentEditable, {
  shouldForwardProp: prop => prop !== 'error' && prop !== 'isComment',
})<ContentEditableProps & { isComment?: boolean }>(({ error, theme, isComment }) => ({
  ...theme.components?.MuiInputBase?.styleOverrides,
  backgroundColor: colors.white,
  borderStyle: 'solid',
  borderWidth: 1,
  borderRadius: spacing(1),
  borderColor: error ? colors.redPrimary : colors.grayExtraLight,
  minHeight: '100%',
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  // Conditional padding: more top padding for non-comments to fix cursor position
  padding: isComment ? spacing(0, 2, 4) : spacing(2, 2, 4),
  [onHover]: {
    borderColor: error ? colors.redPrimary : colors.blueMedium,
  },
  [onFocus]: {
    borderColor: error ? colors.redPrimary : colors.blueDark,
    borderWidth: 1,
    outlineWidth: 0,
  },
  '& .mention': {
    color: colors.bluePrimary,
    fontWeight: 600,
    cursor: 'pointer',
  },
  '& p': {
    marginTop: 0,
  },
}))

const Placeholder = styled(Box, {
  shouldForwardProp: prop => prop !== 'isComment',
})<{ isComment?: boolean }>(({ isComment }) => ({
  padding: isComment ? spacing(2) : spacing(2, 2, 2), // Match content-editable padding
  position: 'absolute',
  top: 0,
  left: 0,
  userSelect: 'none',
  pointerEvents: 'none',
  color: colors.grayMedium,
  opacity: 1,
  display: 'inline-block',
}))

const wrapperStyle: SxProps = {
  position: 'relative',
  background: 'transparent',
  minHeight: 160,
  display: 'flex',
  flex: '1 1 auto',
}

const commentWrapperStyle: SxProps = {
  direction: 'row',
  justifyContent: 'center',
  width: '100%',
  minHeight: 56,
}

const commentContentEditableStyle = (isInputActive: boolean) => ({
  scrollbarWidth: 'none',
  borderRadius: spacing(4),
  '& p': {
    margin: '15px 0px',
  },
  padding: !isInputActive ? spacing(0, 6, 0, 2) : spacing(0, 0, 6, 2),
})

type MentionsTextareaModel = string | object | null | undefined

type MentionsTextareaProps = PropsWithChildren & {
  elementId: string
  value: string
  ariaLabel: string
  placeholder?: React.ReactNode
  error?: boolean
  onChange: (value: MentionsTextareaModel) => void
  isComment?: boolean
  isMentionsSupported?: boolean
  onFormSubmit?: () => void
  isInputActive?: boolean
  autoFocus?: boolean
  isSubmitOnEnterDisabled?: boolean
}

const MentionsTextarea: FC<MentionsTextareaProps> = ({
  elementId,
  value = '',
  ariaLabel,
  placeholder,
  error = false,
  onChange,
  isComment,
  children,
  isMentionsSupported = false,
  onFormSubmit,
  isInputActive = false,
  autoFocus = false,
  isSubmitOnEnterDisabled = false,
}) => {
  const [areMentionsOpen, setAreMentionsOpen] = useState(false)
  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const raw = $generateHtmlFromNodes(editor, null)
      if (!!onChange) {
        onChange(raw)
      }
    })
  }

  // Catch any errors that occur during Lexical updates and log them
  // or throw them as needed. If you don't throw them, Lexical will
  // try to recover gracefully without losing user data.
  const onError = (_error: unknown) => {
    return
  }

  const initialConfig = {
    namespace: 'KudosEditor',
    nodes: [MentionNode],
    defaultValue: value,
    onError,
  }

  // Plugin to handle proper cursor positioning on focus

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Stack id={elementId} sx={{ ...wrapperStyle, ...(isComment ? commentWrapperStyle : null) }}>
        <RichTextPlugin
          contentEditable={
            <MuiContentEditable
              ariaLabel={ariaLabel}
              error={error}
              isComment={isComment}
              sx={{ ...(isComment ? commentContentEditableStyle(isInputActive) : null) }}
            />
          }
          placeholder={<Placeholder isComment={isComment}>{placeholder ?? ''}</Placeholder>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <KudosToolbar>{children}</KudosToolbar>
        {isMentionsSupported && <MentionsPlugin setAreMentionsOpen={setAreMentionsOpen} />}
        <HistoryPlugin />
        <SetValuePlugin value={value} />
        <OnChangePlugin onChange={handleChange} />
        {areMentionsOpen || (
          <SubmitOnEnterPlugin onEnter={onFormSubmit} disabled={isSubmitOnEnterDisabled} />
        )}
        <FocusPlugin />
        {autoFocus && <AutoFocusPlugin />}
      </Stack>
    </LexicalComposer>
  )
}

export default MentionsTextarea
